/** 포인트 잔액 · 내역의 화면 낱말과 줄 조립(웹 `W-70` · 앱 `A-40` · `A-43` · `A-43d`).
 *
 * ⚠️ **이 파일은 `apps/web/src/features/points/point-labels.ts` 와
 * `apps/mobile/src/features/points/point-labels.ts` 에 글자 그대로 한 벌씩 있다.** 정본 자리는
 * `packages/shared`(`billing-history.ts` 처럼)인데 그 자리는 `data-manager` 소관이라 이 회차가 쓰지
 * 못했다. 두 벌이 갈리면 `scripts/points-view.test.mjs` 가 빨개진다. shared 로 올리면 두 벌을 지운다.
 *
 * **낱말의 정본은 PRD `7.5.9` 「포인트의 낱말」(2026-10-27 `design-manager`)이다.** 목업이 이미 쓰는
 * 말만 쓴다: `포인트` · `남은 포인트` · `포인트 내역` · 갈래 `충전` · `씀` · `돌아옴` · `시작 전 취소`.
 * 목업에 없던 넷은 2026-10-27 사용자 결정이다: `충전 환불` · `충전 환불 취소` · `포인트 조정`, 그리고 음수 · 빈 내역 문장.
 * (`충전 환불 요청` 은 다음 회차 버튼의 낱말이라 지금 이 파일에 두지 않는다.)
 * ⛔ `잔액` · `크레딧` · `사용` · `차감` · `환급` · `반환` · `소멸` · `만료` · `유효기간` · `기한` 을 쓰지 않는다.
 *    `환불` 은 `충전 환불` 으로 묶인 자리에서만 쓴다(갈래 이름으로 홀로 쓰지 않는다).
 *
 * **화면은 더하거나 빼지 않는다.** 남은 포인트도 줄 금액도 서버 원장이 낸 값을 그대로 적는다.
 */

import {
  formatDateTime,
  formatKrw,
  parseServerDate,
  pointHistoryGroup,
  type PointEntryKind,
  type PointHistoryItemView,
  type StorePlatform,
} from "@makit/shared";

/** 내역 거르기 칩. 목업 `A-43d` 의 넷 그대로, 이 순서이고 처음엔 `전체`. */
export type PointFilter = "ALL" | "CHARGE" | "SPEND" | "RETURN";

export const POINT_FILTERS: ReadonlyArray<{ key: PointFilter; label: string }> = [
  { key: "ALL", label: "전체" },
  { key: "CHARGE", label: "충전" },
  { key: "SPEND", label: "씀" },
  { key: "RETURN", label: "돌아옴" },
];

/** 줄이 어느 칩에 걸리는가. shared `pointHistoryGroup` 의 셋에 **`SPEND_VOID`(시작 전 취소로 다시
 * 들어온 포인트)를 `돌아옴` 으로 더한다**(2026-10-27 `design-manager`: 남은 포인트로 다시 들어온 줄이고
 * 꼬리 `시작 전 취소` 가 못 만든 장과 가른다).
 *
 * `CHARGE_REFUND`(충전 환불) · `CHARGE_REINSTATE`(충전 환불 취소)는 `충전` 칩에 든다. `ADJUST`(포인트 조정)는
 * `null` 이라 `전체` 칩에서만 보인다(2026-10-27 사용자 결정, 노션 「결정 대기」 행 405ee4004f9481968981ef9a994f685a,
 * 추천안 전부). 어느 줄도 숨기지 않는다. 남은 포인트는 줄들의 합이라 숨기면 등식이 깨진다. */
export function pointFilterGroup(kind: PointEntryKind): Exclude<PointFilter, "ALL"> | null {
  if (kind === "SPEND_VOID") return "RETURN";
  if (kind === "CHARGE_REFUND" || kind === "CHARGE_REINSTATE") return "CHARGE";
  return pointHistoryGroup(kind);
}

export function matchesPointFilter(item: PointHistoryItemView, filter: PointFilter): boolean {
  return filter === "ALL" || pointFilterGroup(item.kind) === filter;
}

/** 빼기표. **붙임표(`-`)가 아니라 U+2212 다**(목업 `−₩4,000`). */
const MINUS = "−";

/** 부호를 붙인 금액. `+₩10,000` · `−₩4,000`. 0 은 부호 없이 `₩0`. */
export function signedPoints(amount: number): string {
  if (amount > 0) return `+${formatKrw(amount)}`;
  if (amount < 0) return `${MINUS}${formatKrw(-amount)}`;
  return formatKrw(0);
}

/** 남은 포인트. 빚이 있으면 서버가 음수로 주지만 **`₩0` 으로 적는다**(목업 `A-43f`, PRD `7.5.9`).
 *
 * ⛔ **음수를 남은 포인트로 적지 않는다.** `−₩3,500` 을 남은 포인트 자리에 두면 셀러가 빚을 남은 돈으로
 * 읽는다. 모자란 몫은 따로 `SHORTFALL_LABEL` 줄이 `shortfallText` 로 적는다. */
export function balanceText(balance: number): string {
  return formatKrw(balance < 0 ? 0 : balance);
}

/** 모자란 몫 줄의 이름. **`A-26p` 가 이미 쓰는 낱말이다**(PRD `7.5.9` 「포인트의 낱말」). */
export const SHORTFALL_LABEL = "모자란 포인트";

/** 모자란 포인트. 남은 포인트가 0 아래일 때만 값이 있다(`₩3,500`. 부호 없이). 아니면 `null` 이고 그 줄을
 *  세우지 않는다. */
export function shortfallText(balance: number): string | null {
  return balance < 0 ? formatKrw(-balance) : null;
}

/** ⛔ **아래 문장을 아직 띄우지 않는다**(법무 `L-F78`-4 · `6A.25`(3), PRD `7.5.9` 「`A-43f` 충전 환불이 생긴 뒤」,
 *  목업 `A-43f` ④). 스토어 환불 뒤 포인트를 거두는 규칙이 **약관에 먼저 실려야** 이 문장의 근거가 선다.
 *  약관 개정이 시행되면 이 값 하나를 `true` 로 바꾼다. `남은 포인트 ₩0` · `모자란 포인트` 두 줄은 이 값과 무관하게 선다. */
export const SHOW_NEGATIVE_BALANCE_NOTE = false;

/** 남은 포인트가 음수일 때 그 줄 아래 문장(2026-10-27 사용자 결정). 빚은 충전 환불로만 생긴다.
 *  띄울지는 위 `SHOW_NEGATIVE_BALANCE_NOTE` 가 정한다. */
export const NEGATIVE_BALANCE_NOTE = "충전 환불로 이미 쓴 포인트만큼 모자랍니다.";

/** 내역이 한 줄도 없을 때의 문장(2026-10-27 사용자 결정). */
export const EMPTY_HISTORY_NOTE = "아직 포인트 내역이 없습니다.";

const PLATFORM_WORD: Record<StorePlatform, string> = {
  APP_STORE: "App Store",
  GOOGLE_PLAY: "Google Play",
};

/** 상품명이 비면 `새 상품`. 청구 내역 줄과 같은 말이다(`13.1` O-8). */
function productWord(item: PointHistoryItemView): string {
  return item.productName ?? "새 상품";
}

/** 스토어 이름을 붙인다. 스토어를 모르면 낱말만 둔다. */
function withPlatform(word: string, item: PointHistoryItemView): string {
  return item.platform ? `${word} · ${PLATFORM_WORD[item.platform]}` : word;
}

/** 줄 이름. `충전 · App Store` · `충전 환불 · App Store` · `충전 환불 취소 · Google Play` ·
 * `무광 세라믹 머그 · 씀` · `무광 세라믹 머그 · 돌아옴` · `포인트 조정`. */
export function pointRowTitle(item: PointHistoryItemView): string {
  switch (item.kind) {
    case "CHARGE":
      return withPlatform("충전", item);
    case "CHARGE_REFUND":
      return withPlatform("충전 환불", item);
    case "CHARGE_REINSTATE":
      return withPlatform("충전 환불 취소", item);
    case "SPEND":
      return `${productWord(item)} · 씀`;
    case "RETURN":
    case "SPEND_VOID":
      return `${productWord(item)} · 돌아옴`;
    case "ADJUST":
      return "포인트 조정";
  }
}

/** 어느 화면의 줄인가. 웹 충전 줄만 꼬리 `앱에서 충전` 이 붙는다(목업 `W-70`). */
export type PointSurface = "app" | "web";

/** 보조줄. `09-06 09:43 · 8장` · `09-06 14:05 · 못 만든 1장` · `09-06 10:02 · 시작 전 취소`.
 *
 * ⛔ 충전 줄에 `MM-DD까지 환불 가능` 을 붙이지 않는다. 7일 창구 문언은 법무 판정(`13.1` O-90) 전이다
 * (2026-10-27 `design-manager`). 앱 충전 줄은 시각만, 웹 충전 줄은 `앱에서 충전` 을 붙인다.
 * 시각이 없으면(못 읽음) 앞 구분자 없이 꼬리만 남긴다. */
export function pointRowDetail(
  item: PointHistoryItemView,
  opts: { year: boolean; surface: PointSurface },
): string {
  const when = formatDateTime(item.occurredAt, { year: opts.year });
  let tail = "";
  switch (item.kind) {
    case "SPEND":
      tail = item.imageCount ? `${item.imageCount}장` : "";
      break;
    case "RETURN":
      tail = item.imageCount ? `못 만든 ${item.imageCount}장` : "";
      break;
    case "SPEND_VOID":
      tail = "시작 전 취소";
      break;
    case "CHARGE":
      tail = opts.surface === "web" ? "앱에서 충전" : "";
      break;
    default:
      tail = "";
  }
  return [when, tail].filter((part) => part !== "").join(" · ");
}

/** `씀`(SPEND) · 못 만든 장으로 `돌아옴`(RETURN) 줄이면 그 작업의 정산 명세로 갈 수 있다. 충전 줄에는 `›` 가 없다.
 *
 * ⛔ **시작 전 취소(SPEND_VOID) 줄에는 `›` 를 두지 않는다**(2026-10-27 코드리뷰). 정산 명세는 작업으로 결제를 찾는데
 * (`by-job`) 그 조회가 취소된 결제를 빼므로, 포인트로만 낸 결제를 시작 전에 취소한 작업은 「찾지 못했습니다」나
 * 다른 결제의 명세가 열린다. 내역 줄이 결제 id 를 싣게 되면(서버 · shared 후속) 그때 다시 단다. */
export function pointRowJobId(item: PointHistoryItemView): string | null {
  return item.kind === "SPEND" || item.kind === "RETURN" ? item.jobId : null;
}

/** 로컬 달력의 `YYYY-MM`. 월 머리글(`2026년 9월`)의 열쇠다. 못 읽은 시각은 빈 문자열. */
export function pointMonthKey(iso: string): string {
  const parsed = parseServerDate(iso);
  if (!parsed) return "";
  const month = parsed.getMonth() + 1;
  return `${parsed.getFullYear()}-${month < 10 ? `0${month}` : `${month}`}`;
}

/** 최신순 줄을 달마다 묶는다. 순서는 받은 그대로다(서버가 최신순으로 준다). 합계는 두지 않는다(목업 `A-43d`). */
export function groupPointsByMonth(
  items: readonly PointHistoryItemView[],
): Array<{ yearMonth: string; items: PointHistoryItemView[] }> {
  const groups: Array<{ yearMonth: string; items: PointHistoryItemView[] }> = [];
  for (const item of items) {
    const key = pointMonthKey(item.occurredAt);
    const last = groups[groups.length - 1];
    if (last && last.yearMonth === key) last.items.push(item);
    else groups.push({ yearMonth: key, items: [item] });
  }
  return groups;
}
